import { useRef, useState, type FormEvent } from 'react';
import { DOCUMENT_KINDS, type DocumentKind } from '@gc/domain';
import type { ApiClient } from '@gc/api-client';
import { useAction } from '@gc/ui-state';
import { words } from './labels.js';

export type DocumentsApi = Pick<ApiClient, 'uploadDocument'>;

export function DocumentUpload({ gadgetId, api, onUploaded }: {
  gadgetId: string;
  api: DocumentsApi;
  onUploaded: () => void;
}) {
  const [kind, setKind] = useState<DocumentKind>('invoice');
  const [description, setDescription] = useState('');
  const file = useRef<HTMLInputElement>(null);
  const { busy, failure, run } = useAction();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const chosen = file.current?.files?.[0];
    if (!chosen) return;
    void run(() => api.uploadDocument(gadgetId, chosen, {
      kind,
      filename: chosen.name,
      contentType: chosen.type || 'application/octet-stream',
      sizeBytes: chosen.size,
      ...(description.trim() ? { description: description.trim() } : {}),
    })).then((ok) => {
      if (!ok) return;
      setDescription('');
      if (file.current) file.current.value = '';
      onUploaded();
    });
  };

  return (
    <form className="form" onSubmit={submit} data-testid="upload-form">
      <h3>Add a document</h3>
      {failure && <p role="alert" data-testid="upload-failure">{failure}</p>}
      <div className="grid">
        <label>Type
          <select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)} data-testid="upload-kind">
            {DOCUMENT_KINDS.map((k) => <option key={k} value={k}>{words(k)}</option>)}
          </select>
        </label>
        <label>File<input ref={file} type="file" required data-testid="upload-file" /></label>
        <label>Description<input value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      </div>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy} data-testid="upload-submit">{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
    </form>
  );
}
